import React, { useState, useEffect, useCallback, useRef } from 'react';
import { T, FONTS, SHADOW } from '../../constants/theme.js';
import {
  fetchArrivals, fetchDepartures, fetchInHouse,
  fetchRoomBoard, fetchDashboardStats, fetchDashboardRevenue,
  checkIn, checkOut,
} from '../../services/api.js';
import { Card, Btn, fmtCurrency, fmtDate, Avatar, Spinner, useOfflineInvalidation } from '../../components/ui.jsx';
import { usePendingSync } from '../../hooks/usePendingSync.js';

/* ─── micro sparkline ─────────────────────────────────────────────────────── */
function Spark({ data = [], color = T.teal, h = 36, w = 90 }) {
  if (data.length < 2) return null;
  const max = Math.max(...data, 1), min = Math.min(...data, 0), rng = max - min || 1;
  const px = i => (i / (data.length - 1)) * w;
  const py = v => h - ((v - min) / rng) * (h - 4) - 2;
  const pts = data.map((v, i) => `${px(i)},${py(v)}`).join(' ');
  const lx  = px(data.length - 1), ly = py(data[data.length - 1]);
  return (
    <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} style={{ overflow:'visible', display:'block' }}>
      <defs>
        <linearGradient id={`sg${color.replace(/[^a-z]/gi,'')}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity=".22" />
          <stop offset="100%" stopColor={color} stopOpacity=".01" />
        </linearGradient>
      </defs>
      <polygon points={`0,${h} ${pts} ${w},${h}`} fill={`url(#sg${color.replace(/[^a-z]/gi,'')})`} />
      <polyline points={pts} fill="none" stroke={color} strokeWidth="1.6"
        strokeLinejoin="round" strokeLinecap="round" />
      <circle cx={lx} cy={ly} r="3" fill={color} stroke="white" strokeWidth="1.5" />
    </svg>
  );
}

/* ─── area chart ─────────────────────────────────────────────────────────── */
function AreaChart({ data = [], prev = [], currency, color = T.teal }) {
  const [hov, setHov] = useState(null);
  if (data.length < 2) return (
    <div style={{ height:160, display:'flex', alignItems:'center', justifyContent:'center', color:T.muted, fontSize:12 }}>
      No revenue data yet
    </div>
  );
  const W = 600, H = 150;
  const all  = [...data.map(d => d.v), ...prev.map(d => d.v)];
  const max  = Math.max(...all, 1);
  const tx   = i => (i / (data.length - 1)) * W;
  const ty   = v => H - (v / max) * (H - 16) - 8;
  const pts  = data.map((d, i) => `${tx(i)},${ty(d.v)}`);
  const ppts = prev.map((d, i) => `${tx(i)},${ty(d.v)}`);
  const fmt  = v => fmtCurrency(v, currency);
  return (
    <div style={{ position:'relative', paddingTop:32 }}>
      {hov !== null && (
        <div style={{
          position:'absolute', top:0, left:`clamp(0px, calc(${(hov/(data.length-1))*100}% - 80px), calc(100% - 160px))`,
          background:T.navy, color:T.white, padding:'5px 12px', borderRadius:8,
          fontSize:11, fontWeight:700, whiteSpace:'nowrap', zIndex:10, pointerEvents:'none',
          boxShadow:SHADOW.md,
        }}>
          {data[hov]?.label} · {fmt(data[hov]?.v)}
          {prev[hov] && <> · <span style={{ color:`${T.gold}BB` }}>prev {fmt(prev[hov].v)}</span></>}
        </div>
      )}
      <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none"
        style={{ width:'100%', height:H, overflow:'visible', display:'block' }}>
        <defs>
          <linearGradient id="ag1" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={color} stopOpacity=".28" />
            <stop offset="100%" stopColor={color} stopOpacity=".02" />
          </linearGradient>
        </defs>
        {/* prev period dashed line */}
        {prev.length > 1 && (
          <polyline points={ppts.join(' ')} fill="none" stroke={T.gold}
            strokeWidth="1.5" strokeDasharray="5,4" opacity=".55" strokeLinejoin="round" />
        )}
        {/* current fill */}
        <polygon points={`0,${H} ${pts.join(' ')} ${W},${H}`} fill="url(#ag1)" />
        <polyline points={pts.join(' ')} fill="none" stroke={color}
          strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
        {hov !== null && (
          <>
            <line x1={tx(hov)} y1={0} x2={tx(hov)} y2={H}
              stroke={`${color}50`} strokeWidth={1} strokeDasharray="4,3" />
            <circle cx={tx(hov)} cy={ty(data[hov].v)} r={5}
              fill={color} stroke="white" strokeWidth={2} />
          </>
        )}
        <rect x={0} y={0} width={W} height={H} fill="transparent"
          onMouseMove={e => {
            const r = e.currentTarget.closest('svg').getBoundingClientRect();
            const idx = Math.max(0, Math.min(data.length - 1,
              Math.round(((e.clientX - r.left) / r.width) * (data.length - 1))));
            setHov(idx);
          }}
          onMouseLeave={() => setHov(null)} />
      </svg>
      {/* x-axis labels */}
      <div style={{ display:'flex', justifyContent:'space-between', marginTop:6 }}>
        {data.filter((_, i) => i % Math.ceil(data.length / 7) === 0 || i === data.length - 1)
          .map((d, i) => (
            <span key={i} style={{ fontSize:9, color:T.muted }}>{d.label}</span>
          ))}
      </div>
    </div>
  );
}

/* ─── donut chart ─────────────────────────────────────────────────────────── */
function Donut({ segs, size = 120, center, sub }) {
  const [hov, setHov] = useState(null);
  const total = segs.reduce((a, s) => a + s.v, 0) || 1;
  let off = 0;
  const r = (size - 28) / 2, cx = size / 2, cy = size / 2, circ = 2 * Math.PI * r;
  return (
    <div style={{ position:'relative', width:size, height:size }}>
      <svg width={size} height={size} style={{ transform:'rotate(-90deg)' }}>
        {segs.map((s, i) => {
          const dash = (s.v / total) * circ;
          const el = (
            <circle key={i} cx={cx} cy={cy} r={r} fill="none" stroke={s.color}
              strokeWidth={hov === i ? 20 : 16}
              strokeDasharray={`${dash} ${circ - dash}`}
              strokeDashoffset={-off * circ / total}
              style={{ transition:'stroke-width .15s', cursor:'pointer' }}
              onMouseEnter={() => setHov(i)} onMouseLeave={() => setHov(null)} />
          );
          off += s.v; return el;
        })}
        <circle cx={cx} cy={cy} r={r - 10} fill="white" />
      </svg>
      <div style={{ position:'absolute', inset:0, display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', pointerEvents:'none' }}>
        {hov !== null ? (
          <>
            <span style={{ fontSize:17, fontWeight:800, color:segs[hov].color, fontFamily:FONTS.display }}>{segs[hov].v}</span>
            <span style={{ fontSize:8, color:T.muted, maxWidth:44, textAlign:'center', lineHeight:1.2 }}>{segs[hov].label}</span>
          </>
        ) : (
          <>
            <span style={{ fontSize:21, fontWeight:800, color:T.navy, fontFamily:FONTS.display, lineHeight:1 }}>{center}</span>
            <span style={{ fontSize:8, color:T.muted, letterSpacing:.6, textTransform:'uppercase' }}>{sub}</span>
          </>
        )}
      </div>
    </div>
  );
}

/* ─── KPI metric card ────────────────────────────────────────────────────── */
function KpiCard({ label, value, sub, trend, up, spark, color, icon, onClick }) {
  const [hovered, setHovered] = useState(false);
  return (
    <div onClick={onClick}
      onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)}
      style={{
        background:T.white, borderRadius:14,
        border:`1px solid ${hovered && onClick ? color+'50' : T.border}`,
        padding:'14px 16px', cursor:onClick ? 'pointer' : 'default',
        transition:'all .2s', position:'relative', overflow:'hidden',
        boxShadow: hovered && onClick ? `0 4px 20px ${color}25` : SHADOW.sm,
        minWidth:0,
      }}>
      {/* top color bar */}
      <div style={{ position:'absolute', top:0, left:0, right:0, height:3, background:color, opacity:.85 }} />
      {/* sparkline background */}
      <div style={{ position:'absolute', right:8, bottom:8, opacity:.22, pointerEvents:'none' }}>
        <Spark data={spark || []} color={color} h={30} w={64} />
      </div>
      <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', marginBottom:8 }}>
        <span style={{ fontSize:10, fontWeight:700, color:T.muted, letterSpacing:.7, textTransform:'uppercase',
          overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', maxWidth:'80%' }}>{label}</span>
        <span style={{ fontSize:15, lineHeight:1, flexShrink:0 }}>{icon}</span>
      </div>
      <p style={{ fontFamily:FONTS.display, fontSize:24, fontWeight:700, color:T.navy, lineHeight:1, marginBottom:5 }}>{value}</p>
      <div style={{ display:'flex', alignItems:'center', gap:5 }}>
        {trend !== undefined && (
          <span style={{ fontSize:9, fontWeight:700, padding:'2px 6px', borderRadius:99,
            color: up ? T.success : T.danger,
            background: up ? T.successPale : T.dangerPale, flexShrink:0 }}>
            {up ? '↑' : '↓'} {Math.abs(trend)}%
          </span>
        )}
        {sub && <span style={{ fontSize:10, color:T.muted, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{sub}</span>}
      </div>
    </div>
  );
}

/* ─── guest row ──────────────────────────────────────────────────────────── */
function GuestRow({ res, type, onAction, actioning }) {
  return (
    <div style={{ display:'flex', alignItems:'center', gap:10, padding:'10px 0', borderBottom:`1px solid ${T.border}` }}>
      <Avatar name={res.guestName} size={34} color={type === 'arr' ? T.teal : T.warning} />
      <div style={{ flex:1, minWidth:0 }}>
        <p style={{ fontSize:12, fontWeight:700, color:T.navy, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
          {res.guestName || 'Guest'}
        </p>
        <p style={{ fontSize:10, color:T.muted }}>
          {res.confirmationNumber} · {res.adults || 1} adult{res.adults !== 1 ? 's' : ''} · Rm {res.roomNumber || '—'}
        </p>
      </div>
      {type === 'arr' && res.status === 'checked_in' ? (
        <span style={{ fontSize:11, fontWeight:700, color:T.success,
          background:`${T.success}15`, padding:'4px 10px', borderRadius:8 }}>
          ✓ Checked In
        </span>
      ) : (
        <Btn size="sm" variant={type === 'arr' ? 'primary' : 'navy'}
          disabled={actioning === res.id} onClick={() => onAction(res)}>
          {actioning === res.id ? '…' : type === 'arr' ? '✓ Check In' : '✓ Check Out'}
        </Btn>
      )}
    </div>
  );
}

/* ─── live feed item ─────────────────────────────────────────────────────── */
function FeedItem({ icon, color, title, detail, isLast }) {
  return (
    <div style={{ display:'flex', gap:11, paddingBottom: isLast ? 0 : 10, marginBottom: isLast ? 0 : 10, borderBottom: isLast ? 'none' : `1px solid ${T.border}` }}>
      <div style={{ width:32, height:32, borderRadius:9, background:`${color}14`, display:'flex', alignItems:'center', justifyContent:'center', fontSize:14, flexShrink:0, marginTop:1 }}>
        {icon}
      </div>
      <div style={{ flex:1, minWidth:0 }}>
        <p style={{ fontSize:12, fontWeight:600, color:T.navy, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{title}</p>
        <p style={{ fontSize:10, color:T.muted, marginTop:1 }}>{detail}</p>
      </div>
      <div style={{ width:7, height:7, borderRadius:'50%', background:color, alignSelf:'center', flexShrink:0 }} />
    </div>
  );
}

/* ─── MAIN DASHBOARD ─────────────────────────────────────────────────────── */
export default function Dashboard({ property, onNavigate }) {
  const [arrivals,   setArrivals]   = useState([]);
  const [departures, setDepartures] = useState([]);
  const [inhouse,    setInhouse]    = useState([]);
  const [rooms,      setRooms]      = useState([]);
  const [stats,      setStats]      = useState(null);
  const [revenue,    setRevenue]    = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [actioning,  setActioning]  = useState(null);
  const [toast,      setToast]      = useState('');
  const [now,        setNow]        = useState(new Date());
  const [chartPeriod, setChartPeriod] = useState(14);
  const pid      = property?.id;
  const currency = property?.currency || 'USD';

  const { pendingCount, hasPending } = usePendingSync();

  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 30000);
    return () => clearInterval(t);
  }, []);

  const showToast = msg => { setToast(msg); setTimeout(() => setToast(''), 3500); };

  const load = useCallback(() => {
    if (!pid) return;
    setLoading(true);
    Promise.all([
      fetchArrivals(pid).catch(() => []),
      fetchDepartures(pid).catch(() => []),
      fetchInHouse(pid).catch(() => []),
      fetchRoomBoard(pid).catch(() => []),
      fetchDashboardStats(pid).catch(() => null),
      fetchDashboardRevenue(pid, 30).catch(() => []),
    ]).then(([a, d, ih, r, st, rev]) => {
      setArrivals(Array.isArray(a) ? a : []);
      setDepartures(Array.isArray(d) ? d : []);
      setInhouse(Array.isArray(ih) ? ih : []);
      setRooms(Array.isArray(r) ? r : []);
      // Guard null — stats may be missing when offline and not yet cached.
      // All consumers use optional-chaining (stats?.x) so setting {} is safe.
      setStats(st && typeof st === 'object' ? st : {});
      setRevenue(Array.isArray(rev) ? rev : []);
    }).finally(() => setLoading(false));
  }, [pid]);

  useEffect(() => { load(); }, [load]);
  useOfflineInvalidation('reservations', load);
  useOfflineInvalidation('dashboard',    load);
  useOfflineInvalidation('housekeeping', load);
  useOfflineInvalidation('rooms',        load);

  const handleIn  = async r => { setActioning(r.id); try { await checkIn(r.id);  showToast(`✅ ${r.guestName} checked in`);  load(); } catch(e){ showToast(`❌ ${e.message}`); } finally { setActioning(null); } };
  const handleOut = async r => { setActioning(r.id); try { await checkOut(r.id); showToast(`✅ ${r.guestName} checked out`); load(); } catch(e){ showToast(`❌ ${e.message}`); } finally { setActioning(null); } };

  /* derived */
  const rByStatus  = rooms.reduce((a, r) => { a[r.status] = (a[r.status] || 0) + 1; return a; }, {});
  const totalRooms = rooms.length;
  const occPct     = totalRooms ? Math.round(((rByStatus.occupied || 0) / totalRooms) * 100) : 0;

  const revSlice = revenue.slice(-chartPeriod);
  const prevSlice = revenue.slice(-chartPeriod * 2, -chartPeriod);
  const chartData = revSlice.map(r => ({ label: new Date(r.date).toLocaleDateString('en-GB',{day:'numeric',month:'short'}), v: r.revenue || 0 }));
  const prevData  = prevSlice.map(r => ({ v: r.revenue || 0 }));
  const spark7    = revenue.slice(-7).map(r => r.revenue || 0);
  // Cap trend at ±999% to avoid absurd numbers when previous period was near-zero
  const capTrend  = v => Math.min(999, Math.max(-999, isFinite(v) ? v : 0));
  const revTrend  = spark7.length >= 2 && spark7[0] > 0
    ? capTrend(Math.round(((spark7[spark7.length-1] - spark7[0]) / spark7[0]) * 100))
    : 0;

  const donutSegs = [
    { label:'Available',   v: rByStatus.available   || 0, color: T.success },
    { label:'Occupied',    v: rByStatus.occupied    || 0, color: T.danger  },
    { label:'Dirty',       v: rByStatus.dirty       || 0, color: T.warning },
    { label:'Inspected',   v: rByStatus.inspected   || 0, color: T.teal    },
    { label:'Maintenance', v: rByStatus.maintenance || 0, color: T.muted   },
  ].filter(s => s.v > 0);

  /* live feed items */
  // Only show arrivals that haven't checked in yet (confirmed) — checked_in guests
  // belong in the inhouse section only.  Collect ids from arrivals+departures so
  // the inhouse slice doesn't double-show someone already listed above.
  const _feedArrDepIds = new Set([...arrivals, ...departures].map(r => r.id));
  const feedItems = [
    ...arrivals
      .filter(r => r.status !== 'checked_in')
      .slice(0, 3)
      .map(r  => ({ icon:'🛎️', color:T.teal,    title:`${r.guestName||'Guest'} arriving`,   detail:`Room ${r.roomNumber||'TBA'} · today` })),
    ...departures.slice(0, 3).map(r => ({ icon:'🚪', color:T.warning, title:`${r.guestName||'Guest'} departing`,  detail:`Room ${r.roomNumber||'TBA'} · today` })),
    ...inhouse
      .filter(r => !_feedArrDepIds.has(r.id))
      .slice(0, 2)
      .map(r   => ({ icon:'🏠', color:T.success, title:`${r.guestName||'Guest'} in-house`,   detail:`Room ${r.roomNumber||'—'} · until ${fmtDate(r.departureDate)}` })),
  ].slice(0, 9);

  const timeStr = now.toLocaleTimeString('en-GB', { hour:'2-digit', minute:'2-digit' });
  const dateStr = now.toLocaleDateString('en-GB', { weekday:'long', day:'numeric', month:'long', year:'numeric' });

  if (!property) return null;

  return (
    <div className="fade-up">
      <style>{`
        @keyframes pulse{0%,100%{transform:scale(1);opacity:1}50%{transform:scale(1.3);opacity:.5}}
        @keyframes slideUp{from{transform:translateY(12px);opacity:0}to{transform:translateY(0);opacity:1}}
        .db-btn-period { padding:5px 12px; border-radius:7px; border:1px solid; font-size:10px; font-weight:700; cursor:pointer; transition:all .15s; }
      `}</style>

      {/* ── Pending offline changes notice ─────────────────────────────── */}
      {hasPending && (
        <div style={{
          background:`${T.gold}14`, border:`1px solid ${T.gold}44`,
          borderRadius:10, padding:'9px 16px', marginBottom:14,
          display:'flex', alignItems:'center', gap:10, fontSize:12, color:'#92400e',
        }}>
          <span style={{ fontSize:16 }}>⏳</span>
          <span>
            <strong>{pendingCount} offline change{pendingCount > 1 ? 's' : ''}</strong> pending sync —
            figures below may not yet reflect your recent offline actions.
          </span>
        </div>
      )}

      {/* ── HEADER ─────────────────────────────────────────────────────── */}
      <div style={{
        background:`linear-gradient(135deg, ${T.navy} 0%, ${T.navyLight} 100%)`,
        borderRadius:18, padding:'22px 28px', marginBottom:20, color:T.white,
        display:'flex', justifyContent:'space-between', alignItems:'center',
        flexWrap:'wrap', gap:14, boxShadow:SHADOW.lg,
      }}>
        <div>
          <div style={{ display:'flex', alignItems:'center', gap:9, marginBottom:5 }}>
            <div style={{ width:8, height:8, borderRadius:'50%', background:'#4ADE80', animation:'pulse 2s infinite' }} />
            <span style={{ fontSize:10, fontWeight:700, color:`${T.gold}`, letterSpacing:2, textTransform:'uppercase' }}>
              Live · {timeStr}
            </span>
          </div>
          <h1 style={{ fontFamily:FONTS.display, fontSize:'clamp(22px,4vw,34px)', fontWeight:700, color:T.white, lineHeight:1.1, marginBottom:4 }}>
            {property.name}
          </h1>
          <p style={{ fontSize:12, color:`${T.white}80` }}>
            {dateStr} · {[property.city, property.country].filter(Boolean).join(', ')}
          </p>
        </div>
        <div style={{ display:'flex', gap:10, flexWrap:'wrap' }}>
          <button onClick={() => onNavigate('new-reservation')} style={{
            background:`linear-gradient(135deg, ${T.gold}, ${T.goldLight})`,
            border:'none', borderRadius:11, padding:'10px 20px',
            fontSize:13, fontWeight:800, color:T.navy, cursor:'pointer',
            fontFamily:FONTS.body, letterSpacing:.2, boxShadow:`0 4px 16px ${T.gold}50`,
          }}>+ New Reservation</button>
          <button onClick={load} style={{
            background:`${T.white}18`, border:`1px solid ${T.white}30`,
            borderRadius:11, padding:'10px 16px', fontSize:12, fontWeight:600,
            color:T.white, cursor:'pointer', fontFamily:FONTS.body,
          }}>↻ Refresh</button>
        </div>
      </div>

      {loading ? (
        <div style={{ display:'flex', alignItems:'center', justifyContent:'center', height:320, gap:12 }}>
          <Spinner /><span style={{ color:T.muted }}>Loading dashboard…</span>
        </div>
      ) : (
        <>
          {/* ── ALL-TIME REVENUE BANNER ────────────────────────────────── */}
          <div style={{
            background:`linear-gradient(135deg, ${T.gold}22 0%, ${T.gold}08 100%)`,
            border:`1.5px solid ${T.gold}40`, borderRadius:14, padding:'14px 22px',
            marginBottom:14, display:'flex', justifyContent:'space-between', alignItems:'center',
          }}>
            <div style={{ display:'flex', alignItems:'center', gap:14 }}>
              <div style={{ width:42, height:42, borderRadius:12, background:`${T.gold}22`, display:'flex', alignItems:'center', justifyContent:'center', fontSize:20 }}>💎</div>
              <div>
                <p style={{ fontSize:10, fontWeight:700, color:T.gold, letterSpacing:1.2, textTransform:'uppercase', marginBottom:2 }}>All-Time Total Revenue</p>
                <p style={{ fontFamily:FONTS.display, fontSize:28, fontWeight:800, color:T.navy, lineHeight:1 }}>
                  {fmtCurrency(stats?.totalRevenue || 0, currency)}
                </p>
                <div style={{ display:'flex', gap:14, marginTop:4 }}>
                  <span style={{ fontSize:11, color:T.muted }}>🏨 Rooms: <strong style={{ color:T.navy }}>{fmtCurrency(stats?.totalRoomRevenue || 0, currency)}</strong></span>
                  <span style={{ fontSize:11, color:T.muted }}>🍽️ Other charges: <strong style={{ color:T.navy }}>{fmtCurrency(stats?.totalChargesRevenue || 0, currency)}</strong></span>
                </div>
              </div>
            </div>
            <div style={{ textAlign:'right' }}>
              <p style={{ fontSize:11, color:T.muted, marginBottom:4 }}>Since first booking</p>
              <Btn size="sm" variant="ghost" onClick={() => onNavigate('reports')}>Full Reports →</Btn>
            </div>
          </div>

          {/* ── KPI ROW — 6 cards (responsive via .r-kpi-6) ──────────── */}
          <div className="r-kpi-6">
            <KpiCard icon="🛎️" label="Arrivals Today"
              value={arrivals.length}
              sub={`${arrivals.filter(r=>r.status==='checked_in').length} checked in`}
              color={T.teal}    spark={[2,3,1,arrivals.length,2,3,arrivals.length+1]}
              onClick={() => onNavigate('frontdesk')} />
            <KpiCard icon="🚪" label="Departures"
              value={departures.length}
              sub={`${departures.filter(r=>r.status==='checked_out').length} checked out`}
              color={T.warning} spark={[1,2,4,departures.length,1,2,departures.length]}
              onClick={() => onNavigate('frontdesk')} />
            <KpiCard icon="🏨" label="In-House"
              value={inhouse.length}
              sub={`of ${totalRooms} rooms`}
              color={T.success} spark={[inhouse.length-1,inhouse.length,inhouse.length+1,inhouse.length]}
              onClick={() => onNavigate('reservations')} />
            <KpiCard icon="📊" label="Occupancy"
              value={`${occPct}%`}
              sub={`${rByStatus.available||0} available`}
              color={T.purple}  spark={[55,62,68,72,Math.max(0,occPct-2),occPct]}
              onClick={() => onNavigate('rooms')} />
            <KpiCard icon="💰" label="Today's Revenue"
              value={fmtCurrency(stats?.todayRevenue||0,currency)}
              color={T.gold}    spark={spark7.length ? spark7 : [0]}
              trend={Math.abs(revTrend)} up={revTrend>=0} />
            <KpiCard icon="🧹" label="HK Pending"
              value={stats?.pendingHKTasks||0}
              sub="tasks"
              color={T.danger}  spark={[5,8,3,stats?.pendingHKTasks||0,6,2]}
              onClick={() => onNavigate('housekeeping')} />
          </div>

          {/* ── ARRIVALS / DEPARTURES ───────────────────────────────────── */}
          <div className="r-dash-2col" style={{ display:'grid', gridTemplateColumns:'repeat(2,minmax(0,1fr))', gap:14, marginBottom:14 }}>
            {[
              { type:'arr', label:'🛎️ Arrivals Today', count:arrivals.length, color:T.teal, data:arrivals },
              { type:'dep', label:'🚪 Departures Today', count:departures.length, color:T.warning, data:departures },
            ].map(({ type, label, count, color, data: rows }) => (
              <div key={type} style={{ background:T.white, border:`1px solid ${T.border}`, borderRadius:16, overflow:'hidden', boxShadow:SHADOW.sm }}>
                <div style={{ padding:'14px 18px', borderBottom:`1px solid ${T.border}`, display:'flex', justifyContent:'space-between', alignItems:'center', background:`linear-gradient(135deg,${color}08,${color}03)` }}>
                  <div>
                    <p style={{ fontSize:13, fontWeight:700, color:T.navy }}>{label}</p>
                    <p style={{ fontSize:10, color:T.muted, marginTop:2 }}>
                      {count} guest{count !== 1 ? 's' : ''}
                    </p>
                  </div>
                  <Btn size="sm" variant="ghost" onClick={() => onNavigate('frontdesk')}>View All</Btn>
                </div>
                <div style={{ padding:'4px 18px', maxHeight:246, overflowY:'auto' }}>
                  {rows.length === 0
                    ? <p style={{ textAlign:'center', padding:'24px 0', color:T.muted, fontSize:12 }}>None today</p>
                    : rows.slice(0, 5).map(r => (
                        <GuestRow key={r.id} res={r} type={type} onAction={type==='arr'?handleIn:handleOut} actioning={actioning} />
                      ))}
                </div>
              </div>
            ))}
          </div>

          {/* ── REVENUE CHART + ROOM DONUT + LIVE FEED ─────────────────── */}
          <div className="r-dash-3col" style={{ display:'grid', gridTemplateColumns:'minmax(0,2fr) minmax(0,1fr) minmax(0,1fr)', gap:14, marginBottom:14 }}>

            {/* Revenue area chart */}
            <div style={{ background:T.white, border:`1px solid ${T.border}`, borderRadius:16, padding:'20px 22px', boxShadow:SHADOW.sm }}>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:16 }}>
                <div>
                  <p style={{ fontSize:13, fontWeight:700, color:T.navy }}>💰 Revenue Trend</p>
                  <p style={{ fontSize:10, color:T.muted, marginTop:2 }}>vs prior period (dashed)</p>
                </div>
                <div style={{ display:'flex', gap:5 }}>
                  {[7, 14, 30].map(p => (
                    <button key={p} className="db-btn-period"
                      onClick={() => setChartPeriod(p)}
                      style={{
                        borderColor: chartPeriod === p ? T.teal : T.border,
                        background:  chartPeriod === p ? T.tealPale : 'transparent',
                        color:       chartPeriod === p ? T.teal : T.muted,
                        fontFamily:  FONTS.body,
                      }}>
                      {p}d
                    </button>
                  ))}
                  <Btn size="sm" variant="ghost" style={{ fontSize:10 }} onClick={() => onNavigate('reports')}>
                    Reports →
                  </Btn>
                </div>
              </div>
              <AreaChart data={chartData} prev={prevData} currency={currency} color={T.teal} />
              <div style={{ display:'flex', gap:14, marginTop:8 }}>
                <div style={{ display:'flex', alignItems:'center', gap:5 }}>
                  <div style={{ width:20, height:2, background:T.teal, borderRadius:1 }} />
                  <span style={{ fontSize:10, color:T.muted }}>This period</span>
                </div>
                <div style={{ display:'flex', alignItems:'center', gap:5 }}>
                  <div style={{ width:20, height:2, background:T.gold, borderRadius:1, opacity:.55 }} />
                  <span style={{ fontSize:10, color:T.muted }}>Prior period</span>
                </div>
              </div>
            </div>

            {/* Room status donut */}
            <div style={{ background:T.white, border:`1px solid ${T.border}`, borderRadius:16, padding:'20px 22px', boxShadow:SHADOW.sm, display:'flex', flexDirection:'column' }}>
              <p style={{ fontSize:13, fontWeight:700, color:T.navy, marginBottom:14 }}>🏨 Room Status</p>
              <div style={{ display:'flex', justifyContent:'center', marginBottom:14 }}>
                {totalRooms > 0
                  ? <Donut segs={donutSegs.length ? donutSegs : [{label:'No rooms',v:1,color:T.border}]} size={126} center={`${occPct}%`} sub="Occupied" />
                  : <p style={{ color:T.muted, fontSize:12, textAlign:'center', padding:16 }}>No rooms</p>}
              </div>
              <div style={{ flex:1, display:'flex', flexDirection:'column', gap:5 }}>
                {[
                  { label:'Available',   c:rByStatus.available  ||0, color:T.success },
                  { label:'Occupied',    c:rByStatus.occupied   ||0, color:T.danger  },
                  { label:'Dirty',       c:rByStatus.dirty      ||0, color:T.warning },
                  { label:'Inspected',   c:rByStatus.inspected  ||0, color:T.teal    },
                  { label:'Maintenance', c:rByStatus.maintenance||0, color:T.muted   },
                ].map(s => (
                  <div key={s.label} style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                    <div style={{ display:'flex', alignItems:'center', gap:7 }}>
                      <div style={{ width:8, height:8, borderRadius:2, background:s.color }} />
                      <span style={{ fontSize:11, color:T.muted }}>{s.label}</span>
                    </div>
                    <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                      <div style={{ height:4, borderRadius:99, background:`${s.color}45`, width: Math.max(4, (s.c / (totalRooms || 1)) * 60) }} />
                      <span style={{ fontSize:11, fontWeight:700, color:T.navy, minWidth:20, textAlign:'right' }}>{s.c}</span>
                    </div>
                  </div>
                ))}
              </div>
              <Btn size="sm" variant="ghost" style={{ width:'100%', marginTop:14 }} onClick={() => onNavigate('rooms')}>
                View Board
              </Btn>
            </div>

            {/* Live feed */}
            <div style={{ background:T.white, border:`1px solid ${T.border}`, borderRadius:16, overflow:'hidden', boxShadow:SHADOW.sm, display:'flex', flexDirection:'column' }}>
              <div style={{ padding:'14px 18px', borderBottom:`1px solid ${T.border}`, display:'flex', justifyContent:'space-between', alignItems:'center', background:`linear-gradient(135deg,${T.navy}07,${T.teal}05)` }}>
                <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                  <div style={{ width:7, height:7, borderRadius:'50%', background:'#4ADE80', animation:'pulse 1.8s infinite' }} />
                  <span style={{ fontSize:12, fontWeight:700, color:T.navy }}>Live Feed</span>
                </div>
                <span style={{ fontSize:9, color:T.muted, fontWeight:600 }}>{timeStr}</span>
              </div>
              <div style={{ padding:'12px 16px', overflowY:'auto', flex:1 }}>
                {feedItems.length === 0
                  ? <p style={{ textAlign:'center', padding:'20px 0', color:T.muted, fontSize:12 }}>No activity</p>
                  : feedItems.map((f, i) => (
                      <FeedItem key={i} {...f} isLast={i === feedItems.length - 1} />
                    ))}
              </div>
            </div>
          </div>

          {/* ── IN-HOUSE GUESTS ─────────────────────────────────────────── */}
          {inhouse.length > 0 && (
            <div style={{ background:T.white, border:`1px solid ${T.border}`, borderRadius:16, overflow:'hidden', boxShadow:SHADOW.sm }}>
              <div style={{ padding:'14px 20px', borderBottom:`1px solid ${T.border}`, display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                <p style={{ fontSize:13, fontWeight:700, color:T.navy }}>🏠 In-House ({inhouse.length})</p>
                <Btn size="sm" variant="ghost" onClick={() => onNavigate('reservations')}>View All</Btn>
              </div>
              <div style={{ padding:'14px 18px', display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(215px,1fr))', gap:8, maxHeight:180, overflowY:'auto' }}>
                {inhouse.map(r => (
                  <div key={r.id} style={{ display:'flex', alignItems:'center', gap:9, padding:'9px 12px', background:T.surface, borderRadius:11, border:`1px solid ${T.border}` }}>
                    <Avatar name={r.guestName} size={30} />
                    <div style={{ flex:1, minWidth:0 }}>
                      <p style={{ fontSize:12, fontWeight:700, color:T.navy, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{r.guestName}</p>
                      <p style={{ fontSize:10, color:T.muted }}>Rm {r.roomNumber||'—'} · out {fmtDate(r.departureDate)}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}

      {/* Toast */}
      {toast && (
        <div style={{ position:'fixed', bottom:24, right:24, zIndex:9999, background:toast.startsWith('❌')?T.danger:T.success, color:T.white, padding:'12px 22px', borderRadius:13, fontSize:13, fontWeight:600, boxShadow:SHADOW.lg, animation:'slideUp .25s ease' }}>
          {toast}
        </div>
      )}
    </div>
  );
}